import { Crumbs } from '../components/Crumbs';

/**
 * Страница тарифов ProfileCompany.
 *
 * Контент основан на knowledge/PRICING.md в репозитории. Не требует backend
 * запросов — полностью статичная (на этом этапе кнопки CTA ведут на стандартные
 * action handlers через query string, реальный checkout появится после auth flow).
 *
 * Аудитория: B2C (Pro/Business для ритейла) и B2B (Enterprise для агентств).
 *
 * Mobile-first: 4 тарифа в grid auto-fit minmax(260px, 1fr) → на узких
 * экранах одна колонка, на широких 4 в ряд.
 */
export function Pricing() {
  return (
    <main className="container" style={{ paddingTop: 32 }}>
      <Crumbs items={[{ label: 'Главная', href: '#/' }, { label: 'Тарифы' }]} />

      <header style={{ textAlign: 'center', marginTop: 24, marginBottom: 32 }}>
        <h1 style={{ fontSize: 'clamp(28px, 4vw, 44px)', margin: 0 }}>Тарифы</h1>
        <p
          style={{
            color: 'var(--muted, #6b7287)',
            fontSize: 'clamp(14px, 1.5vw, 17px)',
            maxWidth: 640,
            margin: '12px auto 0',
          }}
        >
          Выберите план под ваш сценарий проверки контрагентов. Все планы включают
          7-дневный пробный период без оплаты.
        </p>
      </header>

      {/* Personal/B2C tiers */}
      <section aria-label="Тарифы для розничных пользователей">
        <h2 style={sectionTitle}>Для физлиц и малого бизнеса</h2>
        <div style={gridStyle}>
          {RETAIL_PLANS.map((plan) => (
            <PlanCard key={plan.code} plan={plan} />
          ))}
        </div>
      </section>

      {/* B2B tiers */}
      <section aria-label="Тарифы для агентств" style={{ marginTop: 56 }}>
        <h2 style={sectionTitle}>Для агентств и крупных команд</h2>
        <div style={gridStyle}>
          {B2B_PLANS.map((plan) => (
            <PlanCard key={plan.code} plan={plan} />
          ))}
        </div>
      </section>

      {/* API tiers */}
      <section aria-label="API доступ" style={{ marginTop: 56 }}>
        <h2 style={sectionTitle}>API-доступ для интеграций</h2>
        <div style={gridStyle}>
          {API_PLANS.map((plan) => (
            <PlanCard key={plan.code} plan={plan} />
          ))}
        </div>
      </section>

      {/* FAQ */}
      <section aria-label="Часто задаваемые вопросы" style={{ marginTop: 56, marginBottom: 56 }}>
        <h2 style={sectionTitle}>Частые вопросы</h2>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12, marginTop: 16 }}>
          {FAQ.map((q) => (
            <details
              key={q.q}
              style={{
                background: 'var(--surface, #ffffff)',
                border: '1px solid var(--border, #e6e2d6)',
                borderRadius: 8,
                padding: '12px 16px',
              }}
            >
              <summary
                style={{
                  cursor: 'pointer',
                  fontWeight: 600,
                  fontSize: 15,
                  color: 'var(--navy-700, #0b1736)',
                }}
              >
                {q.q}
              </summary>
              <p style={{ marginTop: 12, marginBottom: 0, color: 'var(--text, #0e1320)', lineHeight: 1.6 }}>
                {q.a}
              </p>
            </details>
          ))}
        </div>
      </section>
    </main>
  );
}

interface Plan {
  code: string;
  name: string;
  price: string;
  pricePeriod: string;
  description: string;
  features: string[];
  highlight?: boolean;
  ctaText: string;
  ctaTone: 'primary' | 'secondary' | 'gold';
}

const RETAIL_PLANS: Plan[] = [
  {
    code: 'free',
    name: 'Free',
    price: '0 ₽',
    pricePeriod: 'навсегда',
    description: 'Базовая проверка для частного использования',
    features: [
      'Поиск компаний по ИНН/ОГРН/названию',
      '5 проверок в день',
      'Базовая карточка (без рисков и санкций)',
      'Без сохранения истории',
    ],
    ctaText: 'Начать бесплатно',
    ctaTone: 'secondary',
  },
  {
    code: 'pro',
    name: 'Pro',
    price: '1 990 ₽',
    pricePeriod: 'в месяц',
    description: 'Полная проверка для физлиц и самозанятых',
    features: [
      'Безлимитные проверки',
      'Полная карточка с риск-скорингом',
      'Все санкционные списки (15 источников)',
      'Финансы, налоги, реестры',
      'PDF-отчёты',
      'История проверок',
      'Email-уведомления об изменениях',
    ],
    highlight: true,
    ctaText: '7 дней бесплатно',
    ctaTone: 'gold',
  },
  {
    code: 'business',
    name: 'Business',
    price: '4 990 ₽',
    pricePeriod: 'в месяц',
    description: 'Для предпринимателей и небольших команд',
    features: [
      'Всё из Pro',
      'До 5 пользователей',
      'Сравнение до 5 контрагентов',
      'Bulk-проверка (до 500 ИНН за раз)',
      'Подписки на изменения с Telegram-алертами',
      'API: 10 000 запросов/мес',
      'Приоритетная поддержка',
    ],
    ctaText: '7 дней бесплатно',
    ctaTone: 'primary',
  },
  {
    code: 'reputation',
    name: 'Reputation',
    price: '2 990 ₽',
    pricePeriod: 'в месяц',
    description: 'Для управления репутацией компании',
    features: [
      'Право ответа на отзывы (от верифицированной компании)',
      'AI-черновик ответа на негатив',
      'Dispute (оспаривание неточных отзывов)',
      'Дашборд динамики рейтинга',
      'Подписка на новые отзывы',
      'NB: честные негативные отзывы НЕ удаляются',
    ],
    ctaText: '7 дней бесплатно',
    ctaTone: 'primary',
  },
];

const B2B_PLANS: Plan[] = [
  {
    code: 'enterprise',
    name: 'Enterprise',
    price: 'от 25 000 ₽',
    pricePeriod: 'в месяц',
    description: 'Для агентств, юристов, банков и риск-департаментов',
    features: [
      'Всё из Business',
      'Безлимит пользователей',
      'White-label интерфейс с вашим брендом',
      'API: 100 000+ запросов/мес',
      'Глубокий граф связей (директора, учредители)',
      'Кастомные риск-модели под вашу отрасль',
      'SLA 99.9%, выделенный менеджер',
      'Договор оферты, закрывающие документы',
    ],
    ctaText: 'Запросить демо',
    ctaTone: 'primary',
  },
];

const API_PLANS: Plan[] = [
  {
    code: 'api_starter',
    name: 'API Starter',
    price: '4 990 ₽',
    pricePeriod: 'в месяц',
    description: 'Для разработчиков, тестирующих интеграции',
    features: [
      '10 000 запросов/мес',
      'Все public endpoints',
      'JSON/REST',
      'OpenAPI Swagger UI',
      'Sandbox для тестов',
    ],
    ctaText: '7 дней бесплатно',
    ctaTone: 'secondary',
  },
  {
    code: 'api_business',
    name: 'API Business',
    price: '14 990 ₽',
    pricePeriod: 'в месяц',
    description: 'Для production-интеграций',
    features: [
      '100 000 запросов/мес',
      'Webhooks на изменения',
      'Bulk endpoints',
      'Приоритетный rate-limit',
      'Поддержка в Slack/Telegram',
    ],
    highlight: true,
    ctaText: '7 дней бесплатно',
    ctaTone: 'gold',
  },
  {
    code: 'api_enterprise',
    name: 'API Enterprise',
    price: 'индивидуально',
    pricePeriod: '',
    description: 'High-load и custom-требования',
    features: [
      '1M+ запросов/мес',
      'Dedicated infrastructure (SLA 99.99%)',
      'Custom endpoints под ваши задачи',
      'On-premise / private cloud',
      'Архитектурное сопровождение',
    ],
    ctaText: 'Связаться с нами',
    ctaTone: 'primary',
  },
];

const FAQ = [
  {
    q: 'Откуда берутся данные?',
    a: 'Только из открытых государственных источников: ФНС (МСП, Прозрачный бизнес, реестры дисквалифицированных), ЦБ РФ, Росаккредитация (1.6M+ сертификатов и деклараций), Минцифры (32K записей реестра российского ПО), 15 санкционных списков (OFAC, EU, UK HMT, UN SC и др.), ОКВЭД2 справочник, СРО реестры и многие другие. Никаких посредников и серых баз.',
  },
  {
    q: 'Можно ли удалить негативный отзыв?',
    a: 'Нет. Честные отзывы основанные на реальном опыте взаимодействия с компанией не удаляются — это противоречит миссии сервиса. План Reputation покупает не удаление, а контроль процесса: право ответа от верифицированного представителя, AI-черновик корректного ответа, dispute неточностей.',
  },
  {
    q: 'Что включает 7-дневный trial?',
    a: 'Полный функционал выбранного плана без ограничений. По окончании trial — автоматическое списание (можно отключить в личном кабинете). Карта оформляется при старте trial.',
  },
  {
    q: 'Как часто обновляются данные?',
    a: 'Зависит от источника. Курсы ЦБ — ежедневно. Санкционные списки — ежедневно. ФНС МСП — ежемесячно (12-го числа). FSA сертификаты — ежемесячно. Минцифры ПО — еженедельно. Каждый источник имеет timer + freshness check в /readyz endpoint, никаких stale snapshot не отдаётся.',
  },
  {
    q: 'Соблюдается ли 152-ФЗ?',
    a: 'Да. ИНН физлиц маскируется для незарегистрированных пользователей, сервис зарегистрирован в реестре операторов ПДн (Роскомнадзор). Полные сведения о персональных данных — в политике конфиденциальности.',
  },
  {
    q: 'Можно ли получить договор и закрывающие документы?',
    a: 'Да, на планах Business, Reputation и Enterprise. Оплата по счёту, акт+счёт-фактура (или УПД) ежемесячно. Документы автоматически в личном кабинете.',
  },
];

const sectionTitle: React.CSSProperties = {
  fontFamily: 'Fraunces, Georgia, serif',
  fontSize: 'clamp(20px, 2.5vw, 26px)',
  margin: '0 0 16px',
  color: 'var(--navy-700, #0b1736)',
  textAlign: 'center',
};
const gridStyle: React.CSSProperties = {
  display: 'grid',
  gap: 16,
  gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))',
  alignItems: 'stretch',
};

function PlanCard({ plan }: { plan: Plan }) {
  return (
    <article
      style={{
        background: 'var(--surface, #ffffff)',
        border: plan.highlight
          ? '2px solid var(--gold-500, #c79a2c)'
          : '1px solid var(--border, #e6e2d6)',
        borderRadius: 12,
        padding: 20,
        position: 'relative',
        display: 'flex',
        flexDirection: 'column',
        minWidth: 0,
      }}
    >
      {plan.highlight && (
        <span
          style={{
            position: 'absolute',
            top: -10,
            left: 16,
            background: 'var(--gold-500, #c79a2c)',
            color: '#1a1a1a',
            padding: '2px 10px',
            borderRadius: 999,
            fontSize: 11,
            fontWeight: 600,
            textTransform: 'uppercase',
            letterSpacing: '0.04em',
          }}
        >
          Популярный
        </span>
      )}
      <h3
        style={{
          fontFamily: 'Fraunces, Georgia, serif',
          fontSize: 22,
          margin: 0,
          color: 'var(--navy-700, #0b1736)',
        }}
      >
        {plan.name}
      </h3>
      <div style={{ marginTop: 8, marginBottom: 8 }}>
        <span
          style={{
            fontFamily: 'JetBrains Mono, monospace',
            fontSize: 24,
            fontWeight: 600,
            color: 'var(--navy-700, #0b1736)',
          }}
        >
          {plan.price}
        </span>
        {plan.pricePeriod && (
          <span style={{ color: 'var(--muted, #6b7287)', fontSize: 14, marginLeft: 6 }}>
            / {plan.pricePeriod}
          </span>
        )}
      </div>
      <p
        style={{
          color: 'var(--muted, #6b7287)',
          fontSize: 14,
          margin: '0 0 16px',
          lineHeight: 1.5,
          minHeight: 42,
        }}
      >
        {plan.description}
      </p>
      <ul
        style={{
          listStyle: 'none',
          padding: 0,
          margin: '0 0 20px',
          flex: 1,
          display: 'flex',
          flexDirection: 'column',
          gap: 8,
        }}
      >
        {plan.features.map((f) => (
          <li key={f} style={{ display: 'flex', gap: 8, fontSize: 13, color: 'var(--text, #0e1320)' }}>
            <span style={{ color: 'var(--gold-500, #c79a2c)', flexShrink: 0 }}>✓</span>
            <span style={{ overflowWrap: 'anywhere' }}>{f}</span>
          </li>
        ))}
      </ul>
      <a
        href="#/checkout"
        data-cta={`pricing-${plan.code}`}
        style={{
          display: 'inline-block',
          padding: '12px 18px',
          textAlign: 'center',
          textDecoration: 'none',
          borderRadius: 8,
          fontWeight: 600,
          fontSize: 14,
          ...(plan.ctaTone === 'gold'
            ? { background: 'var(--gold-500, #c79a2c)', color: '#1a1a1a' }
            : plan.ctaTone === 'primary'
              ? { background: 'var(--navy-700, #0b1736)', color: '#ffffff' }
              : {
                  background: 'transparent',
                  color: 'var(--navy-700, #0b1736)',
                  border: '1px solid var(--border, #e6e2d6)',
                }),
        }}
      >
        {plan.ctaText}
      </a>
    </article>
  );
}
